/** Inline searchbar widget: template suggestions until the first message, then chat. */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage as ChatMessageData, DisplayFormData, WebchatConfig } from './types';
import type { MessageTemplate as WidgetMessageTemplate } from '../types';
import type { BrainWebchatApiClient } from './brainApi';
import type { BrainPreviewApiClient } from './brainPreviewApi';
import { useWidgetChat } from './useWidgetChat';
import { useAutoScroll } from '../hooks/useAutoScroll';
import { SEARCHBAR_DEFAULT_PRIMARY, searchbarThemeVars } from './searchbarTheme';
import { useT } from '../i18n';
import { SearchbarPill } from '../components/Searchbar/SearchbarPill';
import { SearchbarInitialScreen } from '../components/Searchbar/SearchbarInitialScreen';
import { ThinkingTimeline } from '../components/Searchbar/ThinkingTimeline';
import { ChatMessage } from '../components/ChatMessage/ChatMessage';
import { QuickActions } from '../components/QuickActions/QuickActions';
import { FormSubmissionBubble } from '../components/FormSubmissionBubble/FormSubmissionBubble';
import { DynamicFormDrawer } from '../components/DynamicFormDrawer/DynamicFormDrawer';
import { InlineContactSection } from '../components/InlineContactSection/InlineContactSection';
import { isFormSubmitted } from './formState';

import styles from './SearchbarWidget.module.css';
import panelStyles from '../components/Searchbar/ConversationPanel.module.css';

interface SearchbarWidgetProps {
  webchatConfig: WebchatConfig;
  apiClient: BrainWebchatApiClient | BrainPreviewApiClient;
  /** Preview mode disables localStorage */
  previewMode?: boolean;
}

export function SearchbarWidget({
  webchatConfig,
  apiClient,
  previewMode = false,
}: SearchbarWidgetProps) {
  const t = useT();
  const [inputValue, setInputValue] = React.useState('');
  const [templatesCollapsed, setTemplatesCollapsed] = useState(false);

  const {
    messages,
    isStreaming,
    error,
    sendMessage,
    clearChat,
    activeForm,
    activeFormSource,
    submittedFormIds,
    openForm,
    closeForm,
    submitForm,
    triggerForm,
    toolCallHistory,
    quickActions,
    isQuickActionsLoading,
  } = useWidgetChat({ apiClient, paramFields: webchatConfig.paramFields });

  const inputRef = useRef<HTMLInputElement>(null);

  const { containerRef, scrollToBottom, handleScroll } = useAutoScroll({
    // The chips too: on the Brain transport they arrive after the turn has
    // ended, so neither of the others changes when they do.
    deps: [messages, isStreaming, quickActions],
  });

  // No "open form" button here, so the latest unsubmitted form auto-opens to
  // keep the hook's activeForm in sync; per-message tracking lets a re-offered
  // repeatable form open again.
  const inlineForm = useMemo<{ form: DisplayFormData; messageId: string } | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      const form = message.displayForm;
      if (form) {
        return isFormSubmitted(message, submittedFormIds)
          ? null
          : { form, messageId: message.id };
      }
    }
    return null;
  }, [messages, submittedFormIds]);

  useEffect(() => {
    if (!inlineForm) return;
    // Don't clobber a form the user opened via a CTA / quick-action / carousel /
    // topButton (any non-inline source with a live activeForm). Only auto-open
    // the pending inline form when no such form is on screen.
    if (activeForm && activeFormSource?.kind !== 'inline') return;
    const openMessageId =
      activeFormSource?.kind === 'inline' ? activeFormSource.messageId : null;
    if (openMessageId !== inlineForm.messageId) {
      openForm(inlineForm.form, inlineForm.messageId);
    }
  }, [inlineForm, activeForm, activeFormSource, openForm]);

  const messageTemplates = useMemo<WidgetMessageTemplate[]>(
    () =>
      (webchatConfig.messageTemplates || []).map((t) => ({
        label: t.label,
        search_query: t.message,
        emoji: t.icon,
        form_id: t.formId,
      })),
    [webchatConfig.messageTemplates],
  );

  const handleTemplateClick = (template: WidgetMessageTemplate) => {
    if (template.form_id) {
      triggerForm(template.form_id);
    } else if (template.search_query) {
      sendMessage(template.search_query);
      scrollToBottom();
    }
  };

  const handleSubmit = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    sendMessage(trimmed);
    setInputValue('');
    scrollToBottom();
  };

  const primary = webchatConfig.primaryColor || SEARCHBAR_DEFAULT_PRIMARY;
  const primaryHover = webchatConfig.primaryHoverColor ?? undefined;
  const rootStyle = searchbarThemeVars(webchatConfig);

  const renderableMessages = messages.filter(
    (msg) =>
      msg.displayCarousel ||
      msg.displayForm ||
      msg.formSubmission ||
      msg.content.trim() !== '',
  );

  const lastMessage = messages[messages.length - 1];
  const isThinking =
    isStreaming &&
    lastMessage?.role === 'assistant' &&
    lastMessage.content.trim() === '' &&
    !lastMessage.displayCarousel &&
    !lastMessage.displayForm;

  const hasConversation = renderableMessages.length > 0 || isStreaming;

  // Group renderable messages into "turns": one user message followed by all
  // assistant messages until the next user message.
  const turns = useMemo<ChatMessageData[][]>(() => {
    const grouped: ChatMessageData[][] = [];
    let current: ChatMessageData[] = [];
    for (const msg of renderableMessages) {
      if (msg.role === 'user' && current.length > 0) {
        grouped.push(current);
        current = [];
      }
      current.push(msg);
    }
    if (current.length > 0) grouped.push(current);
    return grouped;
  }, [renderableMessages]);

  const handleResetClick = () => {
    setInputValue('');
    setTemplatesCollapsed(false);
    clearChat();
  };

  const pill = (
    <SearchbarPill
      value={inputValue}
      placeholder={
        webchatConfig.howCanWeHelpText || t('searchbar.placeholder')
      }
      disabled={isStreaming}
      onInputChange={setInputValue}
      onSubmit={handleSubmit}
      leftAction={
        hasConversation
          ? { kind: 'avatar', aiAvatarUrl: webchatConfig.aiAvatarUrl }
          : {
              kind: 'toggle-templates',
              collapsed: templatesCollapsed,
              onClick: () => setTemplatesCollapsed((c) => !c),
              aiAvatarUrl: webchatConfig.aiAvatarUrl,
              expandIconUrl: webchatConfig.expandIconUrl,
              collapseIconUrl: webchatConfig.collapseIconUrl,
            }
      }
      inputRef={inputRef}
    />
  );

  return (
    <div
      className={`${styles.root}${hasConversation ? ` ${styles.rootChat}` : ''}`}
      style={rootStyle}
    >
      {hasConversation && (
        <div className={styles.resetBar}>
          <button
            type="button"
            className={styles.resetButton}
            onClick={handleResetClick}
          >
            ↻ {t('searchbar.newConversation')}
          </button>
        </div>
      )}
      <div
        className={`${styles.shell}${hasConversation ? ` ${styles.shellChat}` : ''}`}
      >
        {!hasConversation ? (
          activeForm ? (
            <DynamicFormDrawer
              activeForm={activeForm}
              onClose={closeForm}
              onSubmit={submitForm}
              isDisabled={isStreaming}
              primaryColor={primary}
              primaryHoverColor={primaryHover}
              useGradient={webchatConfig.useGradient}
              inline
              showHeader
            />
          ) : (
            <>
              {!templatesCollapsed && (
                <SearchbarInitialScreen
                  logoUrl={webchatConfig.logoUrl}
                  messageTemplates={messageTemplates}
                  onTemplateClick={handleTemplateClick}
                />
              )}
              {pill}
            </>
          )
        ) : (
          <>
            <div
              ref={containerRef}
              className={panelStyles.messages}
              onScroll={handleScroll}
            >
              {turns.map((turn, turnIndex) => {
                const isLastTurn = turnIndex === turns.length - 1;
                return (
                  <div
                    key={turn[0]?.id ?? `turn-${turnIndex}`}
                    className={panelStyles.turn}
                  >
                    {turn.map((msg, msgIndex) => {
                      const isLastInTurn = msgIndex === turn.length - 1;
                      const isLast = isLastTurn && isLastInTurn;
                      const showQuickActions =
                        isLast &&
                        !msg.displayForm &&
                        !isThinking &&
                        webchatConfig.quickActionsEnabled &&
                        (isQuickActionsLoading ||
                          (!isStreaming && quickActions.length > 0));
                      const quickActionsNode = showQuickActions ? (
                        <QuickActions
                          actions={quickActions}
                          isLoading={isQuickActionsLoading}
                          heading={t('searchbar.quickActionsHeading')}
                          onActionClick={(action) => {
                            if (action.form_id) {
                              triggerForm(action.form_id);
                            } else if (action.search_query) {
                              sendMessage(action.search_query);
                              scrollToBottom();
                            }
                          }}
                        />
                      ) : undefined;
                      const formNode =
                        msg.displayForm &&
                        activeForm &&
                        ((activeFormSource?.kind === 'inline' &&
                          activeFormSource.messageId === msg.id) ||
                          (activeFormSource?.kind === 'trigger' &&
                            activeForm.form_id === msg.displayForm.form_id)) ? (
                          <DynamicFormDrawer
                            activeForm={activeForm}
                            onClose={closeForm}
                            onSubmit={submitForm}
                            isDisabled={isStreaming}
                            primaryColor={primary}
                            primaryHoverColor={primaryHover}
                            useGradient={webchatConfig.useGradient}
                            inline
                            showHeader
                            showBackButton={false}
                          />
                        ) : undefined;
                      const isContactFormActive = activeFormSource?.kind === 'topButton';
                      const showContactSection =
                        isLast &&
                        msg.role === 'assistant' &&
                        !msg.displayForm &&
                        !isThinking &&
                        !isStreaming &&
                        !!webchatConfig.topButton &&
                        !submittedFormIds.has(webchatConfig.topButton.formId);
                      const contactSectionNode = showContactSection ? (
                        <>
                          <InlineContactSection
                            label={webchatConfig.topButton!.label}
                            emoji={webchatConfig.topButton!.emoji}
                            description={webchatConfig.topButton!.description}
                            primaryColor={primary}
                            primaryHoverColor={primaryHover}
                            useGradient={webchatConfig.useGradient}
                            isOpen={isContactFormActive}
                            onClick={() => {
                              if (isContactFormActive) {
                                closeForm();
                              } else {
                                triggerForm(
                                  webchatConfig.topButton!.formId,
                                  undefined,
                                  'topButton',
                                );
                              }
                            }}
                          />
                          {isContactFormActive && (
                            <DynamicFormDrawer
                              activeForm={activeForm}
                              onClose={closeForm}
                              onSubmit={submitForm}
                              isDisabled={isStreaming}
                              primaryColor={primary}
                              primaryHoverColor={primaryHover}
                              useGradient={webchatConfig.useGradient}
                              inline
                            />
                          )}
                        </>
                      ) : undefined;
                      const trailingFooter =
                        quickActionsNode || contactSectionNode ? (
                          <>
                            {quickActionsNode}
                            {contactSectionNode}
                          </>
                        ) : undefined;
                      const footerNode = formNode ?? trailingFooter;
                      const hasMessageBody =
                        msg.content.trim() !== '' ||
                        !!msg.displayCarousel ||
                        !!msg.displayForm;
                      return (
                        <div key={msg.id}>
                          {msg.formSubmission && (
                            <FormSubmissionBubble />
                          )}
                          {hasMessageBody && (
                            <ChatMessage
                              role={msg.role}
                              content={msg.content}
                              displayCarousel={msg.displayCarousel}
                              isCarouselBuilding={msg.isCarouselBuilding}
                              carouselVariant="program-card"
                              onCarouselButtonClick={(item, formId) => {
                                triggerForm(
                                  formId,
                                  item as unknown as Record<string, unknown>,
                                );
                              }}
                              footer={footerNode}
                              userAvatarUrl={webchatConfig.userAvatarUrl}
                              hoverArrowUrl={webchatConfig.hoverArrowUrl}
                              previewMode={previewMode}
                              openLinksInNewTab={webchatConfig.openLinksInNewTab}
                            />
                          )}
                        </div>
                      );
                    })}
                    {isLastTurn && isThinking && (
                      <ThinkingTimeline
                        toolCallHistory={toolCallHistory}
                        isActive={isStreaming}
                        logoUrl={webchatConfig.logoUrl ?? undefined}
                        label={webchatConfig.thinkingText ?? undefined}
                      />
                    )}
                  </div>
                );
              })}
              {error && <div className={panelStyles.error}>{error}</div>}
            </div>
            {pill}
          </>
        )}
      </div>
    </div>
  );
}
